import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Document } from 'react-pdf';
import ViewerNavBar from './components/ViewerNavBar/ViewerNavBar';
import ViewerBody from './components/ViewerBody/ViewerBody';
import FooterBar from 'pages/DocumentBuilder/components/FooterBar';
import NotesSideBar from 'pages/DocumentBuilder/components/NotesSideBar';
import { useGetDocumentQuery } from 'features/document/documentApi';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  DocumentType,
  selectDocumentName,
  selectDocumentType,
  selectTemplateDocument,
  updateDocumentName,
  updateDocumentType,
  updateTemplateJson,
  updateNoteTitle,
  updateNoteContent,
  toggleSidebar,
  selectSalesforceIds,
} from 'features/document/documentSlice';
import {
  selectIsSalesforceAuth,
  setContract,
  setQuotes,
  setQuote,
  selectQuotes,
} from 'features/salesforce/salesforceSlice';
import { parseContracts } from 'utils/parseOpportunitiesAndQuotes';
import {
  useGetContractsByOpportunityIdQuery,
  useGetQuotesByOpportunityIdQuery,
} from 'features/salesforce/salesforceApi';
import { useGetAllFilesQuery } from 'features/files/filesApi';
import { QueryBuilder } from 'utils/formatQueryParams';
import NotFound from 'components/NotFound/NotFound';

import styles from './DocumentViewerPage.module.css';

export const DocumentViewerPage = () => {
  const { id } = useParams();
  const selector = useAppSelector;
  const dispatch = useAppDispatch();
  const defaultZoom = 80;

  const { data, error, isLoading } = useGetDocumentQuery(Number(id), {
    skip: id === undefined,
    refetchOnMountOrArgChange: true,
  });

  const isSalesforceAuth = selector(selectIsSalesforceAuth);
  const templateJson = selector(state => selectTemplateDocument(state));
  const documentName = selector(state => selectDocumentName(state));
  const documentType = selector(selectDocumentType);
  const recipients = templateJson.recipients;
  const salesforceIds = selector(selectSalesforceIds);
  const quotes = selector(selectQuotes);

  const [areNotesOpen, setAreNotesOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(templateJson.pages.length > 0 ? 1 : 0);
  const [zoom, setZoom] = useState<number>(defaultZoom);
  const isThumbnailSidebarOpen = selector(state => state.document.isSidebarOpen);
  const pageRefs = useRef(templateJson.pages.map(() => React.createRef<HTMLDivElement>()));
  const thumbnailPagesRef = useRef(templateJson.pages.map(() => React.createRef<HTMLDivElement>()));

  const [activeOpportunity, setActiveOpportunity] = useState<string>('');
  const [activeQuote, setActiveQuote] = useState<string>('');

  const { currentData: dataQuotes } = useGetQuotesByOpportunityIdQuery(activeOpportunity, {
    skip: !isSalesforceAuth || activeOpportunity === '' || documentType !== DocumentType.QUOTE,
  });

  const { currentData: dataContracts } = useGetContractsByOpportunityIdQuery(activeOpportunity, {
    skip: !isSalesforceAuth || activeOpportunity === '' || documentType !== DocumentType.CONTRACT,
  });
  const builder = new QueryBuilder();
  const queryString = builder;

  queryString
    .setQueryWhere({
      field: 'isDocumentPdf',
      operator: '$eq',
      value: '1',
    })
    .setQueryWhere({
      field: 'documentId',
      operator: '$eq',
      value: id,
    });

  const { data: pdfData, isLoading: isLoadingPdf } = useGetAllFilesQuery(queryString.build(), {
    skip: id === undefined,
  });

  useEffect(() => {
    if (data) {
      dispatch(updateTemplateJson({ templateJson: data.data.templateJson }));
      dispatch(updateDocumentName(data.data.name));
      dispatch(updateNoteTitle(data.data.noteTitle));
      dispatch(updateNoteContent(data.data.noteContent));
      dispatch(updateDocumentType(data?.data.type));
    }
  }, [data]);

  useEffect(() => {
    if (pdfData) {
      const { data } = pdfData;
      if (data.length) {
        const [pdfFound] = data;
        setDocumentUrl(pdfFound.url);
      }
    }
  }, [pdfData]);

  useEffect(() => {
    const { opportunityId, quoteId } = salesforceIds;
    setActiveOpportunity(opportunityId || '');
    setActiveQuote(quoteId || '');
  }, [salesforceIds]);

  useEffect(() => {
    if (dataQuotes) {
      const { data } = dataQuotes;
      dispatch(setQuotes(data));
    }
  }, [dataQuotes]);

  useEffect(() => {
    if (dataContracts) {
      const { data } = dataContracts;
      dispatch(setContract(parseContracts(data[0])));
    }
  }, [dataContracts]);

  useEffect(() => {
    const newActiveQuote = quotes.find(quote => quote.Id === activeQuote);
    if (newActiveQuote) {
      dispatch(setQuote(newActiveQuote));
    }
  }, [activeQuote, quotes]);

  useEffect(() => {
    pageRefs.current = templateJson.pages.map(() => React.createRef<HTMLDivElement>());
  }, [templateJson.pages]);

  const handleCloseNotes = () => {
    setAreNotesOpen(false);
  };

  const handleScroll = () => {
    if (isScrolling) return;
    const relativeLimit = 0.15 / (zoom / 100);

    for (let i = 0; i < templateJson.pages.length; i++) {
      const page = document.getElementById(templateJson.pages[i].id);
      if (page) {
        const pagePosition = page.getBoundingClientRect();
        const relativeTop = pagePosition.top / pagePosition.height;

        const shouldUpdatePage = relativeTop < relativeLimit && relativeTop > 0;

        if (shouldUpdatePage) {
          setCurrentPage(i + 1);
        }
      }
    }
  };

  const scrollToPage = (pageNumber: number, fromThumbnail: boolean) => {
    setIsScrolling(true);
    if (fromThumbnail) {
      const page = pageRefs.current[pageNumber];
      setCurrentPage(pageNumber + 1);
      if (page && page.current) {
        page.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    if (!fromThumbnail) {
      const page = pageRefs.current[pageNumber - 1];
      setCurrentPage(pageNumber);
      if (page && page.current) {
        page.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    setTimeout(() => {
      setIsScrolling(false);
    }, 500);
  };

  const handleOpenNotes = () => {
    if (isThumbnailSidebarOpen) {
      dispatch(toggleSidebar());
    }
    setAreNotesOpen(!areNotesOpen);
  };

  const renderSection = () => {
    if (isLoading || isLoadingPdf) {
      return (
        <Box className={styles.Layout}>
          <CircularProgress />
        </Box>
      );
    }
    if (error && 'status' in error && error.status === 404) {
      return (
        <Box className={styles.Layout}>
          <NotFound />
        </Box>
      );
    }
    return (
      <Box className={styles.viewerPage}>
        <ViewerNavBar documentInfo={{ documentName, documentType, recipients }} />
        <Box className={styles.mainLayout}>
          {areNotesOpen && <NotesSideBar isViewMode={true} handleClose={handleCloseNotes} />}
          <Box className={styles.middleLayout}>
            <Box className={styles.pageBody} onScroll={handleScroll}>
              {documentUrl ? (
                <Document file={documentUrl}>
                  <ViewerBody
                    hasPdf={!!documentUrl}
                    currentPage={currentPage}
                    pages={templateJson.pages}
                    pagesRef={pageRefs}
                    thumbnailPagesRef={thumbnailPagesRef}
                    scrollToPage={scrollToPage}
                  />
                </Document>
              ) : (
                <ViewerBody
                  hasPdf={false}
                  currentPage={currentPage}
                  pages={templateJson.pages}
                  pagesRef={pageRefs}
                  thumbnailPagesRef={thumbnailPagesRef}
                  scrollToPage={scrollToPage}
                />
              )}
            </Box>
            <FooterBar
              currentPage={currentPage}
              totalPages={templateJson.pages.length}
              onChangePage={scrollToPage}
              onChangeZoom={setZoom}
              handleOpenNotes={handleOpenNotes}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return <Fragment>{renderSection()}</Fragment>;
};
